import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import useProfileImage from '../../hooks/useProfileImage';

const ALLOWED_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']);
const MAX_SIZE = 5 * 1024 * 1024;

export default function ProfileImageUpload({
  hasProfileImage = false,
  entityType = null,
  entityId = null,
  imageRefreshKey = 0,
  initials = '??',
  onFileSelect,
  onRemove,
  size = 96,
  previewFile = null,
}) {
  const fileInputRef = useRef(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const previewUrl = useMemo(() => previewFile ? URL.createObjectURL(previewFile) : null, [previewFile]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);
  const streamedUrl = useProfileImage(entityType, entityId, hasProfileImage, imageRefreshKey);
  const displayUrl = previewUrl || streamedUrl;

  const validateAndSelect = useCallback((file) => {
    setError('');
    if (!ALLOWED_TYPES.has(file.type)) {
      setError('Invalid format. Use JPG, PNG, GIF, or WebP.');
      return;
    }
    if (file.size > MAX_SIZE) {
      setError(`File too large (${(file.size / 1048576).toFixed(1)}MB). Max 5MB.`);
      return;
    }
    onFileSelect(file);
  }, [onFileSelect]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) validateAndSelect(file);
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className={`relative group cursor-pointer rounded-full transition-all ${dragOver ? 'ring-4 ring-indigo-300 dark:ring-indigo-600' : ''}`}
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        style={{ width: size, height: size }}
      >
        <div
          className="w-full h-full rounded-full bg-indigo-600 flex items-center justify-center overflow-hidden border-4 border-white dark:border-slate-800 shadow-lg"
        >
          {displayUrl ? (
            <img src={displayUrl} alt="Profile" className="w-full h-full object-cover" loading="lazy" decoding="async" />
          ) : (
            <svg className="text-white" style={{ width: size * 0.45, height: size * 0.45 }} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          )}
        </div>
        <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-all">
          <svg className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) validateAndSelect(file);
            e.target.value = '';
          }}
        />
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="text-xs text-indigo-600 dark:text-indigo-400 font-medium hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
        >
          {displayUrl ? 'Change Photo' : 'Upload Photo'}
        </button>
        {displayUrl && onRemove && (
          <>
            <span className="text-gray-300 dark:text-slate-600">|</span>
            <button
              type="button"
              onClick={onRemove}
              className="text-xs text-red-500 dark:text-red-400 font-medium hover:text-red-600 dark:hover:text-red-300 transition-colors"
            >
              Remove
            </button>
          </>
        )}
      </div>

      {error && (
        <p className="text-xs text-red-500 dark:text-red-400 text-center">{error}</p>
      )}

      <p className="text-[11px] text-gray-400 dark:text-slate-500 text-center">
        JPG, PNG, GIF, or WebP &bull; Max 5MB
      </p>
    </div>
  );
}
